import Vue from 'vue'
import api from '@/api'
import format from '@/utils/format'
import status from '@/utils/status'

const VIEWED_COOKIE_KEY = 'viewedProjects'
const COLUMN_VISIBILITY_STORAGE_KEY = 'projectListColumnVisibility'

const DISPOSAL_DUTY_DEPT_NAME = '폐기물분석팀'

function isDisposalAnalysisDutyDept(profile) {
	const deptId = String(profile?.dept_id ?? '').trim()
	return deptId === DISPOSAL_DUTY_DEPT_NAME
}

function getDefaultDisposalColumnVisible(rootGetters) {
	if (rootGetters['user/isAdmin']) return true

	const profile = rootGetters['user/profile'] || {}
	if (isDisposalAnalysisDutyDept(profile)) return true

	return false
}

function readColumnVisibilityFromStorage() {
	try {
		const raw = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY)
		if (!raw) return null
		return JSON.parse(raw)
	} catch {
		return null
	}
}

function writeColumnVisibilityToStorage(columnVisibility) {
	try {
		localStorage.setItem(
			COLUMN_VISIBILITY_STORAGE_KEY,
			JSON.stringify(columnVisibility)
		)
	} catch {
		// ignore quota / private mode
	}
}

const state = {
	params : {
		keyword : '',
		page : 1,
		count : 30,
		filter: {}
	},
	list : [],
	count: 1,
	headers: [],
	columnVisibility: {
		disposalName: false
	},
	statusSummary: [],
	filterList: {
		category: {
			title: '업무 코드',
			options: []
		},
		client: {
			title: '고객사',
			options: []
		},
		disposal: {
			title: '배출 업체',
			options: []
		},
		startDatetime: {
			title: '의뢰일',
			value: '',
			type: 'range'
		},
		department: {
			title: '담당 부서',
			options: []
		},
		endDatetime: {
			title: '시험 종료일',
			value: '',
			type: 'date'
		},
		isSettle: {
			title: '정산',
			options: []
		},
		researchDepartment: {
			title: '시험부서',
			options: []
		},
	},
	client: [],
}

const getters = {
	headers: state => {
		return state.headers
			.filter(header => {
				if (header.headerKey !== 'disposalName') return true
				return state.columnVisibility.disposalName === true
			})
			.map(header => {
			return {
				key: header.headerKey,
				label: header.text,
				isSort: header.isSort,
				sort: header.sort,
				link: header.link,
				formatter: (value, key, item) => {
					if(key.indexOf('Date') >= 0) {
						value = format.date({date: value, onlyDate: true})
					}
					else if(key === 'count') {
						const total = Number(value) || 0
						const plural = Number(item.pluralSampleCount) || 0
						// 수질에 한해 복수시료가 있으면 ProjectTest 와 동일한 형식으로 표기
						if (item.category === 'WATER' && plural > 0) {
							value = `${total - plural}개 (+복수시료 ${plural}개)`
						} else {
							value = `${total}개`
						}
					}
					else if(key == 'status') {
						value = { text: status.text[value], color: status.color[value]}
					}
					else if(key == 'amount') {
						value = `${format.number(value)}원`
					}
					else if(key == 'assignee' || key == 'assigneeResearcher') {
						value = value?.name;
					}
					return value;
				}
			}
		})
	},
	columnVisibility: state => state.columnVisibility,
	list: state => {
		const viewedIds = Vue.$cookies.get(VIEWED_COOKIE_KEY) || []
		return state.list.map(item => {
			const threeDaysAgo = new Date(); threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
			const isRecent = item.createDatetime >= format.date({date: threeDaysAgo, onlyDate: true})
			item.isNew = isRecent && !viewedIds.includes(item.id)
			item.amountModal = '정산입력'
			return item
		})
	},
	count: state => state.count,
	params: state => state.params,
	filterList: state => state.filterList,
	statusSummary: state => state.statusSummary
}

const mutations = {
	setList: (state, data) => {
		state.list = [...data.table.list]
		state.count = data.table.count
		state.headers = [...data.table.header]
	},
	setParams: (state, data) => {
		state.params = {...state.params, ...data}
	},
	resetParams: (state) => {
		state.params = {
			keyword : '',
			page : 1,
			count : 30,
			filter: {}
		}
		state.list = [];
	},
	setFilterList: (state, data) => {
		let keys = Object.keys(data);
		let result = {}
		keys.forEach(key => {
			result[key] = {}
			result[key].title = status.filter[key]
			result[key].options = data[key]
		})
		state.filterList = {...state.filterList, ...result}
	},
	setClientList: (state, data) => {
		state.client = [...data.table.list]
	},
	markAsViewed: (state, id) => {
		const viewedIds = Vue.$cookies.get(VIEWED_COOKIE_KEY) || []
		if (!viewedIds.includes(id)) {
			viewedIds.push(id)
			Vue.$cookies.set(VIEWED_COOKIE_KEY, viewedIds, '3d')
		}
	},
	setStatusSummary: (state, data) => {
		let result = [];
		const priority = status.project.reduce((acc, cur) => {
			acc[cur.value] = cur.order; // 예: { WORK_ACCEPTED: 1, WORK_REQUEST: 2, ... }
			return acc;
		}, {});

		// project에 정의된 항목만 order 순으로
		const keys = Object.keys(data)
			.filter(k => priority[k] != null)   // project에 있는 키만
			.sort((a, b) => priority[a] - priority[b]);
		result.push({
			text: '전체',
			value: ''
		})
		keys.forEach((key) => {
			result.push({
				value: key,
				text: `${status.text[key]}(${data[key]})`
			})
		})
		state.statusSummary = result;
	},
	setColumnVisibility(state, { key, visible }) {
		state.columnVisibility = {
			...state.columnVisibility,
			[key]: visible
		}
	},
	initColumnVisibility(state, payload) {
		state.columnVisibility = {
			...state.columnVisibility,
			...payload
		}
	}
}

/**
 * 담당부서 필터 선택지 — 부서 마스터를 쓰되 실제 업무에 쓰인 값만 남긴다.
 *
 * ── 배경 (2026-08-18 신고) ────────────────────────────────────────────
 * "시험의뢰에서 담당부서 필터 적용할 시에 빈 화면이 나옵니다."
 *
 * 선택지는 부서 마스터(department 테이블, 이번 폐기물 이관에서 신설)에서 오는데
 * 목록 조회는 업무에 문자열로 저장된 project.dept_id 와 정확히 일치하는 것만 찾는다.
 * 운영 실측(2026-08-18) 기준 선택지 10개 중 7개는 해당 업무가 0건이라
 * 고르면 반드시 빈 화면이 됐다 (수질분석팀·토양분석팀·경영지원본부·대기환경실(CS)·
 * 대기환경실(분석)·환경사업본부·환경조사팀). 이관 전에는 선택지를 서버가
 * 실제 데이터에서 DISTINCT 로 만들었기 때문에 이런 일이 없었다.
 *
 * 부서 관리 화면으로 목록을 통제하려던 의도는 살리고 빈 선택지만 없앤다.
 * 이름·정렬은 마스터를 따르고, 서버가 준 "실제 쓰인 값"에 없는 것은 감춘다.
 *
 * 한쪽 목록을 받지 못했으면(오류·빈 응답) 다른 쪽을 그대로 쓴다 —
 * 교집합이 통째로 비어 필터를 못 쓰게 되는 편이 더 나쁘다.
 *
 * @param {Array<{value:string,text:string}>} masterOptions 부서 마스터(DUTY)
 * @param {Array<{value:string,text:string}>} usedOptions   서버가 준 실제 사용값
 */
function intersectDutyOptions(masterOptions, usedOptions) {
	const master = Array.isArray(masterOptions) ? masterOptions : []
	const used = Array.isArray(usedOptions) ? usedOptions : []
	if (!master.length) return used
	const usedValues = new Set(
		used.map(o => String(o?.value ?? '').trim()).filter(Boolean)
	)
	if (!usedValues.size) return master
	return master.filter(o => usedValues.has(String(o?.value ?? '').trim()))
}

// async 됨
const actions = {
	loadList: async ({commit, state}, params) => {
		const res = await api.project.getList(params ? params : state.params);
		commit('setList', res)
		return res?.table?.list;
	},
	loadSimpleList : async ({commit, state}, params) => {
		const res = await api.project.getSimpleList(params ? params : state.params);
		// commit('setList', res)
		return res?.table?.list;
	},
	loadFilterList: async({ commit, dispatch, rootGetters }) => {
		await dispatch('department/load', null, { root: true })
		const res = await api.project.getFilterList()
		const dutyOptions = rootGetters['department/dutySelectOptions']
		commit('setFilterList', {
			...res,
			department: intersectDutyOptions(dutyOptions, res?.department)
		})
	},
	loadClientList: async({commit, state}) => {
		const res = await api.client.getList(state.params);
		commit('setClientList', res)
	},
	loadStatus: async ({commit, state}, params) => {
		// status 필터 제외
		const summaryParams = { ...params };
		if (summaryParams.filter) {
			const { status, ...restFilter } = summaryParams.filter;
			summaryParams.filter = restFilter;
		}
		const res = await api.project.getStatusSummary(summaryParams)
		commit('setStatusSummary', res)
	},
	initListColumnVisibility({ commit, rootGetters }) {
		const stored = readColumnVisibilityFromStorage()
		const defaultVisible = getDefaultDisposalColumnVisible(rootGetters)
		const disposalName =
			stored && typeof stored.disposalName === 'boolean'
				? stored.disposalName
				: defaultVisible
		commit('initColumnVisibility', { disposalName })
	},
	toggleDisposalColumn({ commit, state }, visible) {
		const next = {
			...state.columnVisibility,
			disposalName: visible
		}
		commit('setColumnVisibility', { key: 'disposalName', visible })
		writeColumnVisibilityToStorage(next)
	}
}


export default { namespaced: true, state, getters, mutations, actions }